import { useRecreateResourceGroupMutation } from "@graphql/resources/recreate-resource-group.g";
import {
  type ResourceFragment,
  useResourcesByGroupQuery,
  useResourcesQuery,
} from "@graphql/resources/resources.g";
import { useSetPrimaryResourceMutation } from "@graphql/resources/set-primary-resource.g";
import { useUpdateResourceGroupMutation } from "@graphql/resources/update-resource-group.g";
import { useUpdateResourceMutation } from "@graphql/resources/update-resource.g";
import { useRefetchResourceFaviconMutation } from "@graphql/resources/refetch-resource-favicon.g";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { InfiniteResourceSelect } from "@components/Selects/InfiniteResourceSelect";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Table } from "@design-system/Table/Table";
import tableClasses from "@design-system/Table/Table.module.css";
import { Text } from "@design-system/Text/Text";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { useExpandableResourceTable } from "@utils/hooks/useExpandableResourceTable";
import { ConfigurableTableSystem, TableColumnConfig } from "@utils/table/TableConfigurationSystem";
import { buildGroupedTableData } from "@utils/table/sortingUtils";

import {
  AdminApplicationsTableConfig,
  ApplicationsTableConfig,
  renderCategory,
  renderCreatedAt,
  renderIsForConsumers,
  renderIsSaas,
  renderIsVisible,
  renderMspVitalityScore,
  renderProductDescription,
  renderProductName,
  renderUpdatedAt,
  renderVendorDescription,
  renderVendorName,
} from "./ApplicationsTableConfig";
import { ApplicationTableColumns } from "./UI/ApplicationsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type TableRowData = ResourceFragment & {
  isNestedResource?: boolean;
  parentResourceId?: string;
};

type Props = {
  initialPageIndex?: number;
  search?: string;
  isAdminView?: boolean;
  isSaasFilter?: "all" | "saas" | "non-saas";
  categoryId?: string;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
};

export const ApplicationsTable = ({
  initialPageIndex = 0,
  search,
  isAdminView = false,
  isSaasFilter = "all",
  categoryId,
  sorting: controlledSorting,
  onSortingChange,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [internalSorting, setInternalSorting] = useState<SortingState>([
    { id: ApplicationTableColumns.productName, desc: false }
  ]);

  // Use controlled sorting if provided, otherwise use internal state
  const sorting = controlledSorting ?? internalSorting;
  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (onSortingChange) {
        onSortingChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    [sorting, onSortingChange]
  );
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedTargetResourceId, setSelectedTargetResourceId] = useState<
    string | undefined
  >(undefined);
  const queryClient = useQueryClient();
  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const {
    expandedRows,
    expandedRowsData,
    toggleRowExpansion,
    removeFromExpandedData,
    addToExpandedData,
  } = useExpandableResourceTable<ResourceFragment>(
    useCallback(
      async (resourceGroupId: string) => {
        try {
          const result = await queryClient.fetchQuery({
            queryKey: ["ResourcesByGroup", { resourceGroupId }],
            queryFn: useResourcesByGroupQuery.fetcher({
              resourceGroupId,
            }),
          });

          if (result.resourceGroup?.resources?.edges) {
            const resources = result.resourceGroup.resources.edges
              .map((edge) => edge?.node)
              .filter((node) => node && !node.isPrimaryResource)
              .filter(Boolean) as ResourceFragment[];
            return { resources };
          }
          return null;
        } catch (error) {
          console.error("Failed to fetch nested resources:", error);
          return null;
        }
      },
      [queryClient],
    ),
  );

  const recreateResourceGroupMutation = useRecreateResourceGroupMutation();
  const updateResourceGroupMutation = useUpdateResourceGroupMutation();
  const updateResourceMutation = useUpdateResourceMutation();
  const setPrimaryResourceMutation = useSetPrimaryResourceMutation();
  const refetchResourceFaviconMutation = useRefetchResourceFaviconMutation();

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);
  const debouncedCategoryId = useDebounce(
    categoryId,
    SEARCH_DEBOUNCE_TIME,
  );

  // Calculate isSaas and isVisible filter values
  const { isSaasQueryValue, isVisibleQueryValue } = useMemo(() => {
    if (!isAdminView) {
      // Non-admin view always shows only visible SaaS resources
      return { isSaasQueryValue: true, isVisibleQueryValue: true };
    }

    // Admin view uses the filter value for isSaas
    let isSaasValue: boolean | undefined;
    switch (isSaasFilter) {
      case "saas":
        isSaasValue = true;
        break;
      case "non-saas":
        isSaasValue = false;
        break;
      case "all":
      default:
        isSaasValue = undefined; // Don't filter by isSaas
    }

    // Admin view sees all resources regardless of isVisible
    return { isSaasQueryValue: isSaasValue, isVisibleQueryValue: undefined };
  }, [isAdminView, isSaasFilter]);

  const applicationsQuery = useResourcesQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search: debouncedSearch || undefined,
    isPrimaryResource: true,
    ...(isSaasQueryValue !== undefined ? { isSaas: isSaasQueryValue } : {}),
    ...(isVisibleQueryValue !== undefined ? { isVisible: isVisibleQueryValue } : {}),
    parentCategoryId: debouncedCategoryId || undefined,
    orderBy: ConfigurableTableSystem.generateSortParameter(
      (isAdminView ? AdminApplicationsTableConfig : ApplicationsTableConfig)
        .columns,
      sorting,
    ),
  });


  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isApplicationsLoading = applicationsQuery.isLoading;
  const isApplicationsFetching = applicationsQuery.isFetching;

  const totalCount = applicationsQuery.data?.resources?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [debouncedSearch, debouncedCategoryId, isSaasFilter]);

  const data = useMemo(() => {
    const primaryResources =
      applicationsQuery.data?.resources?.edges
        .map((edge) => edge?.node)
        .filter((application) => !!application) ?? [];

    const baseConfig = isAdminView
      ? AdminApplicationsTableConfig
      : ApplicationsTableConfig;

    return buildGroupedTableData<ResourceFragment, ResourceFragment, TableRowData>(
      primaryResources,
      expandedRows,
      expandedRowsData,
      baseConfig.columns as Record<string, TableColumnConfig<unknown, string>>,
      sorting,
    );
  }, [
    applicationsQuery.data,
    expandedRows,
    expandedRowsData,
    sorting,
    isAdminView,
  ]);

  const configuredColumns = useMemo(() => {
    const baseConfig = isAdminView
      ? AdminApplicationsTableConfig
      : ApplicationsTableConfig;
    const configWithRenderers = {
      ...baseConfig,
      columns: Object.fromEntries(
        Object.entries(baseConfig.columns).map(([key, config]) => [
          key,
          {
            ...config,
            render: (value: unknown, row: TableRowData) => {
              const columnKey = key as ApplicationTableColumns;

              switch (columnKey) {
                case ApplicationTableColumns.productName:
                  return renderProductName(value, row, {
                    isExpanded: expandedRows.has(row.id),
                    onToggleExpansion: () =>
                      toggleRowExpansion(row.id, row.resourceGroup?.id),
                    isNestedResource: row.isNestedResource,
                  });
                case ApplicationTableColumns.vendorName:
                  return renderVendorName(value);
                case ApplicationTableColumns.category:
                  return renderCategory(value, row);
                case ApplicationTableColumns.mspVitalityScore:
                  return renderMspVitalityScore(value);
                case ApplicationTableColumns.isForConsumers:
                  return renderIsForConsumers(value);
                case ApplicationTableColumns.createdAt:
                  return renderCreatedAt(value);
                case ApplicationTableColumns.updatedAt:
                  return renderUpdatedAt(value);
                case ApplicationTableColumns.productDescription:
                  return renderProductDescription(value);
                case ApplicationTableColumns.vendorDescription:
                  return renderVendorDescription(value);
                case ApplicationTableColumns.isSaas:
                  return renderIsSaas(value);
                case ApplicationTableColumns.isVisible:
                  return renderIsVisible(value);
                default:
                  return String(value);
              }
            },
          },
        ]),
      ) as typeof ApplicationsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ApplicationTableColumns[])
            : (baseConfig.defaultColumnOrder as ApplicationTableColumns[]),
        columnVisibility: columnSettings,
      },
    );
  }, [
    data,
    columnOrder,
    columnSettings,
    expandedRows,
    toggleRowExpansion,
    isAdminView,
  ]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<TableRowData, unknown>[] = [...configuredColumns];

    return columns;
  }, [configuredColumns]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  const handleRowSelect = useCallback((rowId: string | undefined) => {
    if (rowId) {
      setIsPanelOpen(true);
    } else {
      setIsPanelOpen(false);
    }
    setSelectedRowId(rowId);
  }, []);

  const isRowSelectable = useCallback((row: TableRowData) => {
    if (!row.isNestedResource && (row.nonPrimaryResourcesCount || 0) > 0) {
      return false;
    }
    return true;
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedRowId(undefined);
    setSelectedTargetResourceId(undefined);
  }, []);

  const handlePanelCancel = useCallback(() => {
    handlePanelClose();
  }, [handlePanelClose]);

  const handlePanelApply = useCallback(async () => {
    if (
      selectedRowId &&
      selectedTargetResourceId &&
      !updateResourceGroupMutation.isPending
    ) {
      try {
        // Find the selected resource to get its parent information for immediate UI cleanup
        const selectedResourceData = data.find(
          (resource) => resource.id === selectedRowId,
        );
        const parentResourceId = selectedResourceData?.parentResourceId;

        const result = await updateResourceGroupMutation.mutateAsync({
          resourceId: selectedRowId,
          targetResourceId: selectedTargetResourceId,
        });

        if (result?.updateResourceGroup?.success) {
          // Clear the nested resource from expanded rows data immediately if it's moving from a nested position
          if (parentResourceId) {
            removeFromExpandedData(selectedRowId, parentResourceId);
          }

          // If moving to an expanded target group, optimistically add it there
          if (expandedRows.has(selectedTargetResourceId)) {
            const movedResource = selectedResourceData;
            if (movedResource) {
              addToExpandedData(movedResource, selectedTargetResourceId);
            }
          }

          // Refresh the data to show updated resource groups
          applicationsQuery.refetch();
          handlePanelClose();
        } else {
          // Handle errors from the mutation
          const errors = result?.updateResourceGroup?.errors;
          if (errors && errors.length > 0) {
            throw new Error(errors.join(", "));
          } else {
            throw new Error("Failed to update resource group");
          }
        }
      } catch (error) {
        console.error("❌ Failed to update resource group:", error);
        // TODO: Show error notification to user
      }
    }
  }, [
    selectedRowId,
    selectedTargetResourceId,
    updateResourceGroupMutation,
    applicationsQuery,
    handlePanelClose,
    data,
    expandedRows,
    removeFromExpandedData,
    addToExpandedData,
  ]);

  const handleRemoveFromGroup = useCallback(async () => {
    if (selectedRowId) {
      try {
        // Find the selected resource to get its parent information
        const selectedResourceData = data.find(
          (resource) => resource.id === selectedRowId,
        );
        const parentResourceId = selectedResourceData?.parentResourceId;

        const result = await recreateResourceGroupMutation.mutateAsync({
          resourceId: selectedRowId,
        });

        if (result?.recreateResourceGroup?.success) {
          // Clear the nested resource from expanded rows data immediately
          if (parentResourceId) {
            removeFromExpandedData(selectedRowId, parentResourceId);
          }

          // Refresh the data to show updated resource groups
          applicationsQuery.refetch();
          handlePanelClose();
        } else {
          // Handle errors from the mutation
          const errors = result?.recreateResourceGroup?.errors;
          if (errors && errors.length > 0) {
            throw new Error(errors.join(", "));
          } else {
            throw new Error("Failed to recreate resource group");
          }
        }
      } catch (error) {
        console.error("❌ Failed to remove resource from group:", error);
        // TODO: Show error notification to user
      }
    }
  }, [
    selectedRowId,
    recreateResourceGroupMutation,
    applicationsQuery,
    handlePanelClose,
    data,
    removeFromExpandedData,
  ]);

  const handleToggleResourceVisibility = useCallback(async () => {
    if (selectedRowId) {
      const selectedResourceData = data.find(
        (resource) => resource.id === selectedRowId,
      );
      const currentIsVisible = selectedResourceData?.isVisible ?? false;
      const newIsVisible = !currentIsVisible;
      const parentResourceId = selectedResourceData?.parentResourceId;

      const result = await updateResourceMutation.mutateAsync({
        id: selectedRowId,
        isVisible: newIsVisible,
      });

      if (result?.updateResource?.success) {
        // Refresh the data to reflect the updated resource
        applicationsQuery.refetch();

        // If this is a nested resource, also refresh the nested resources for its parent
        if (parentResourceId && selectedResourceData?.resourceGroup?.id) {
          const resourceGroupId = selectedResourceData.resourceGroup.id;

          // Invalidate the cache for this resource group
          await queryClient.invalidateQueries({
            queryKey: ["ResourcesByGroup", { resourceGroupId }],
          });

          // If the parent row is expanded, collapse and re-expand to force fresh data
          if (expandedRows.has(parentResourceId)) {
            // Collapse the row first to clear cached data
            toggleRowExpansion(parentResourceId, resourceGroupId);

            // Re-expand after a brief delay to fetch fresh data
            setTimeout(() => {
              toggleRowExpansion(parentResourceId, resourceGroupId);
            }, 100);
          }
        }

        handlePanelClose();
      } else {
        // Handle errors from the mutation
        const errors = result?.updateResource?.errors;
        if (errors && errors.length > 0) {
          throw new Error(errors.join(", "));
        } else {
          throw new Error("Failed to update resource visibility");
        }
      }
    }
  }, [
    selectedRowId,
    updateResourceMutation,
    applicationsQuery,
    handlePanelClose,
    data,
    queryClient,
    expandedRows,
    toggleRowExpansion,
  ]);

  const handleToggleResourceSaaS = useCallback(async () => {
    if (selectedRowId) {
      const selectedResourceData = data.find(
        (resource) => resource.id === selectedRowId,
      );
      const currentIsSaas = selectedResourceData?.isSaas ?? false;
      const newIsSaas = !currentIsSaas;
      const parentResourceId = selectedResourceData?.parentResourceId;

      const result = await updateResourceMutation.mutateAsync({
        id: selectedRowId,
        isSaas: newIsSaas,
      });

      if (result?.updateResource?.success) {
        // Refresh the data to reflect the updated resource
        applicationsQuery.refetch();

        // If this is a nested resource, also refresh the nested resources for its parent
        if (parentResourceId && selectedResourceData?.resourceGroup?.id) {
          const resourceGroupId = selectedResourceData.resourceGroup.id;

          // Invalidate the cache for this resource group
          await queryClient.invalidateQueries({
            queryKey: ["ResourcesByGroup", { resourceGroupId }],
          });

          // If the parent row is expanded, collapse and re-expand to force fresh data
          if (expandedRows.has(parentResourceId)) {
            // Collapse the row first to clear cached data
            toggleRowExpansion(parentResourceId, resourceGroupId);

            // Re-expand after a brief delay to fetch fresh data
            setTimeout(() => {
              toggleRowExpansion(parentResourceId, resourceGroupId);
            }, 100);
          }
        }

        handlePanelClose();
      } else {
        // Handle errors from the mutation
        const errors = result?.updateResource?.errors;
        if (errors && errors.length > 0) {
          throw new Error(errors.join(", "));
        } else {
          throw new Error("Failed to update resource SaaS status");
        }
      }
    }
  }, [
    selectedRowId,
    updateResourceMutation,
    applicationsQuery,
    handlePanelClose,
    data,
    queryClient,
    expandedRows,
    toggleRowExpansion,
  ]);

  const handleRefetchFavicon = useCallback(async () => {
    if (selectedRowId) {
      try {
        const result = await refetchResourceFaviconMutation.mutateAsync({
          resourceId: selectedRowId,
        });

        if (result?.refetchResourceFavicon?.errors?.length) {
          toast.error(result.refetchResourceFavicon.errors[0]?.message || "An error occurred");
        } else {
          toast.success("Favicon refetched successfully");
          applicationsQuery.refetch();
          handlePanelClose();
        }
      } catch (error) {
        toast.error("Failed to refetch favicon");
        console.error("Failed to refetch favicon:", error);
      }
    }
  }, [
    selectedRowId,
    refetchResourceFaviconMutation,
    applicationsQuery,
    handlePanelClose,
  ]);

  const handleSetAsPrimary = useCallback(async () => {
    if (selectedRowId) {
      try {
        const result = await setPrimaryResourceMutation.mutateAsync({
          resourceId: selectedRowId,
        });

        if (result?.setPrimaryResource?.success) {
          // Refresh the data to reflect the updated primary resource
          applicationsQuery.refetch();
          // Also refresh any expanded rows data to update nested resources
          if (expandedRows.size > 0) {
            expandedRows.forEach((resourceGroupId) => {
              queryClient.invalidateQueries({
                queryKey: ["ResourcesByGroup", { resourceGroupId }],
              });
            });
          }
          handlePanelClose();
        } else {
          // Handle errors from the mutation
          const errors = result?.setPrimaryResource?.errors;
          if (errors && errors.length > 0) {
            throw new Error(errors.join(", "));
          } else {
            throw new Error("Failed to set resource as primary");
          }
        }
      } catch (error) {
        console.error("❌ Failed to set resource as primary:", error);
        // TODO: Show error notification to user
      }
    }
  }, [
    selectedRowId,
    setPrimaryResourceMutation,
    applicationsQuery,
    handlePanelClose,
    expandedRows,
    queryClient,
  ]);


  // Get selected resource details
  const selectedResource = useMemo(() => {
    return data.find((resource) => resource.id === selectedRowId);
  }, [data, selectedRowId]);

  const columnVisibilityWithCheckbox = useMemo(
    () => ({
      ...columnSettings,
      select: true,
    }),
    [columnSettings],
  );

  return (
    <>
      <Table<TableRowData, string>
        columns={allColumnsWithActions}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isApplicationsLoading}
        isFetching={isApplicationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={columnVisibilityWithCheckbox}
        getRowClassName={(row) =>
          row.isNestedResource ? tableClasses.nestedRow : undefined
        }
        showCheckboxColumn={true}
        selectedRowId={selectedRowId}
        onRowSelect={handleRowSelect}
        isRowSelectable={isRowSelectable}
        manualSorting={true}
      />

      <SlideInPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        onCancel={handlePanelCancel}
        onApply={handlePanelApply}
        title="Update Resource Group"
        cancelButtonText="Cancel"
        applyButtonText={
          updateResourceGroupMutation.isPending ? "Updating..." : "Update Group"
        }
      >
        <Box direction="column" gap={16}>
          <Box direction="column" gap={8}>
            <Text size={14} weight={600} color="primary">
              Selected Resource
            </Text>
            <Text size={12} color="textSecondary">
              {selectedResource?.productName} ({selectedResource?.vendorName})
            </Text>
          </Box>

          <Box direction="column" gap={8}>
            <Text size={14} weight={600} color="primary">
              Move to Resource Group
            </Text>
            <Text size={12} color="textSecondary">
              Select new resource to place this resource under
            </Text>
            <InfiniteResourceSelect
              value={selectedTargetResourceId}
              onChange={setSelectedTargetResourceId}
              excludeId={selectedRowId}
              placeholder="Search by name, vendor, or description..."
            />
          </Box>

          {selectedResource && !selectedResource.isPrimaryResource && (
            <>
              <Box direction="column" gap={8}>
                <Text size={14} weight={600} color="primary">
                  Set as Primary in Group
                </Text>
                <Text size={12} color="textSecondary">
                  Make this resource the primary resource in its resource group
                </Text>
                <Button
                  variant="outlined"
                  size="md"
                  onClick={handleSetAsPrimary}
                  disabled={setPrimaryResourceMutation.isPending}
                >
                  {setPrimaryResourceMutation.isPending
                    ? "Setting as Primary..."
                    : "Set as Primary"}
                </Button>
              </Box>

              <Box direction="column" gap={8}>
                <Text size={14} weight={600} color="primary">
                  Remove from Group
                </Text>
                <Text size={12} color="textSecondary">
                  Remove this resource from its current group
                </Text>
                <Button
                  variant="outlined"
                  size="md"
                  onClick={handleRemoveFromGroup}
                  disabled={recreateResourceGroupMutation.isPending}
                >
                  {recreateResourceGroupMutation.isPending
                    ? "Removing..."
                    : "Remove from Group"}
                </Button>
              </Box>
            </>
          )}

          <Box direction="column" gap={8}>
            <Text size={14} weight={600} color="primary">
              Is this a SaaS?
            </Text>
            <Text size={12} color="textSecondary">
              {selectedResource?.isSaas
                ? "This resource is marked as SaaS"
                : "This resource is not marked as SaaS"}
            </Text>
            <Button
              variant="outlined"
              size="md"
              onClick={handleToggleResourceSaaS}
              disabled={updateResourceMutation.isPending}
            >
              {updateResourceMutation.isPending
                ? "Updating..."
                : selectedResource?.isSaas
                  ? "Mark as Non-SaaS"
                  : "Mark as SaaS"}
            </Button>
          </Box>

          <Box direction="column" gap={8}>
            <Text size={14} weight={600} color="primary">
              Show Resource
            </Text>
            <Text size={12} color="textSecondary">
              {selectedResource?.isVisible
                ? "This resource is visible in the platform"
                : "This resource is hidden from the platform"}
            </Text>
            <Button
              variant="outlined"
              size="md"
              onClick={handleToggleResourceVisibility}
              disabled={updateResourceMutation.isPending}
            >
              {updateResourceMutation.isPending
                ? "Updating..."
                : selectedResource?.isVisible
                  ? "Hide Resource"
                  : "Show Resource"}
            </Button>
          </Box>

          <Box direction="column" gap={8}>
            <Text size={14} weight={600} color="primary">
              Refetch Favicon
            </Text>
            <Text size={12} color="textSecondary">
              Fetch the latest favicon from the product domain
            </Text>
            <Button
              variant="outlined"
              size="md"
              onClick={handleRefetchFavicon}
              disabled={refetchResourceFaviconMutation.isPending}
            >
              {refetchResourceFaviconMutation.isPending
                ? "Refetching..."
                : "Refetch Favicon"}
            </Button>
          </Box>
        </Box>
      </SlideInPanel>
    </>
  );
};
